import { INLINE_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { ReactElement } from 'react';

/**
 * Cookie Policy (`/cookies`). Public statement of what Syncanix stores
 * in a visitor's browser and why.
 *
 * **Accuracy discipline**: this page reflects what the codebase actually
 * does. The marketing site uses consent-based, privacy-friendly product
 * analytics (PostHog) — nothing analytics-related loads, and no analytics
 * cookie is set, until the visitor accepts the consent banner
 * (`MarketingAnalyticsProvider` + `ConsentBanner`, ADR-0077). It ships no
 * advertising or cross-site tracking. The dashboard (app.syncanix.com) uses
 * Auth0 for login (strictly-necessary session) plus the same consent-gated
 * PostHog. The embedded widget keeps a minimal local chat session (strictly
 * necessary). If the analytics posture changes, this page, the consent
 * surface, and the subprocessor list change in the same PR.
 *
 * **i18n**: every visitor-facing string resolves through `useT()` under
 * the `cookies.*` namespace. This is a binding legal document, so the
 * `LegalTranslationNotice` renders directly under the `<h1>` (it
 * self-hides on the English locale).
 *
 * **Three-layer contract preserved**: pure presentational. No data fetch.
 */
export function Cookies(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('cookies.meta.title'),
    description: t('cookies.meta.description'),
    path: '/cookies',
  });
  return (
    <LegalPage
      breadcrumb={t('cookies.breadcrumb')}
      title={t('cookies.title')}
      intro={t('cookies.intro')}
      lastUpdatedSlug="cookies"
    >
      <section aria-labelledby="cookies-summary" className="mb-10">
        <h2
          id="cookies-summary"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('cookies.summary.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            <strong>{t('cookies.summary.noTracking.label')}</strong>{' '}
            {t('cookies.summary.noTracking.body')}
          </li>
          <li>
            <strong>{t('cookies.summary.necessary.label')}</strong>{' '}
            {t('cookies.summary.necessary.body')}
          </li>
          <li>
            <strong>{t('cookies.summary.noBanner.label')}</strong>{' '}
            {t('cookies.summary.noBanner.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="cookies-marketing" className="mb-10">
        <h2
          id="cookies-marketing"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('cookies.marketing.heading')}
        </h2>
        <p>{t('cookies.marketing.body')}</p>
      </section>

      <section aria-labelledby="cookies-dashboard" className="mb-10">
        <h2
          id="cookies-dashboard"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('cookies.dashboard.heading')}
        </h2>
        <p>
          {t('cookies.dashboard.before')}{' '}
          <Link className={INLINE_LINK} to="/trust/subprocessors">
            {t('cookies.dashboard.link')}
          </Link>
          {t('cookies.dashboard.after')}
        </p>
      </section>

      <section aria-labelledby="cookies-widget" className="mb-10">
        <h2
          id="cookies-widget"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('cookies.widget.heading')}
        </h2>
        <p>
          {t('cookies.widget.before')}{' '}
          <Link className={INLINE_LINK} to="/trust/dpa">
            {t('cookies.widget.link')}
          </Link>
          {t('cookies.widget.after')}
        </p>
      </section>

      <section aria-labelledby="cookies-manage" className="mb-10">
        <h2
          id="cookies-manage"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('cookies.manage.heading')}
        </h2>
        <p>{t('cookies.manage.body')}</p>
      </section>

      <section aria-labelledby="cookies-related" className="mb-10">
        <h2
          id="cookies-related"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('cookies.related.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            <Link className={INLINE_LINK} to="/trust/privacy">
              {t('cookies.related.privacy.link')}
            </Link>{' '}
            {t('cookies.related.privacy.desc')}
          </li>
          <li>
            <Link className={INLINE_LINK} to="/terms">
              {t('cookies.related.terms.link')}
            </Link>{' '}
            {t('cookies.related.terms.desc')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="cookies-contact">
        <h2
          id="cookies-contact"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('cookies.contact.heading')}
        </h2>
        <p>
          {t('cookies.contact.before')}{' '}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>
          .
        </p>
      </section>
    </LegalPage>
  );
}
